import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

// Kept verbatim in sync with the SEGMENT_LABELS map inside SEOHead.jsx so
// the visible nav and JSON-LD BreadcrumbList always use identical strings.
// If you add a slug here, add it there (and vice versa).
const SEGMENT_LABELS = {
  "card-machines": "Card Machines",
  "pax-a920-pro": "PAX A920 Pro",
  "ingenico-dx8000": "Ingenico DX8000",
  "ingenico-move-5000": "Ingenico Move 5000",
  "ingenico-desk-5000": "Ingenico Desk 5000",
  "verifone-t650p": "Verifone T650P",
  "elavon-mobile": "Elavon Mobile",
  "paypal-reader": "PayPal Reader",
  "epos-systems": "EPOS Systems",
  "online-payments": "Online Payments",
  "payment-gateway": "Payment Gateway",
  "virtual-terminal": "Phone Payments",
  "online-ordering": "Online Ordering",
  "merchant-cash-advance": "Merchant Cash Advance",
  "switch-and-save": "Switch & Save",
  "partner-programme": "Partner Programme",
  "purpose-beyond-payments": "Purpose Beyond Payments",
  "refer-a-friend": "Refer a Friend",
  "personal-care": "Personal Care",
  "pet-services": "Pet Services",
  pubs: "Pubs & Bars",
  cafes: "Cafés",
  beauty: "Beauty & Hair",
  "e-commerce": "E-Commerce",
  "cost-of-cash": "Cost of Cash",
  "overpaying-scorecard": "Overpaying Scorecard",
  "hidden-fees-checker": "Hidden Fees Checker",
  "cash-vs-card": "Cash vs Card",
  "switch-vs-stay": "Switch vs Stay",
  "best-card-payment-providers-uk": "Card Payment Providers",
  "card-machine-fees": "Card Machine Fees",
  "statement-review": "Statement Review",
  "payment-finder": "Payment Finder",
  "epos-partner-calculator": "EPOS Partner Calculator",
  "ecom-partner-calculator": "E-commerce Partner Calculator",
  "business-insurance": "Business Insurance",
  "psr-quotation-tool": "PSR Quotation Tool",
  "payment-industry-terms": "Payment Industry Terms",
  "modern-slavery-statement": "Modern Slavery Statement",
  "ico-certificate": "ICO Certificate",
  "cookie-policy": "Cookie Policy",
  "privacy-policy": "Privacy Policy",
  "terms-and-conditions": "Terms & Conditions",
  faqs: "FAQs",
  api: "API",
  epos: "EPOS",
  psr: "PSR",
};

function humanise(seg) {
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
  return seg
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Visible breadcrumb nav — pairs with the BreadcrumbList JSON-LD in SEOHead.
 * Reads the current URL and renders `Home › Section › Page`. Hides itself
 * on the homepage (redundant) and accepts an optional `labels` override
 * when a page needs a custom crumb label (e.g. industry template wants
 * "Pubs & Bars" instead of the default humanised "Pubs").
 *
 * Usage:
 *   <Breadcrumbs />                                // auto from URL
 *   <Breadcrumbs labels={{ pubs: "Pubs & Bars" }} /> // per-page override
 *
 * Styled to sit subtly in hero sections; `className` prop adds room for a
 * dark-variant in future.
 */
export default function Breadcrumbs({
  labels = {},
  className = "",
  variant = "light", // "light" (default, for light backgrounds) | "dark" (white-on-dark heroes)
  testId = "breadcrumbs",
}) {
  const { pathname } = useLocation();
  if (!pathname || pathname === "/") return null;

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const crumbs = [];
  let path = "";
  segments.forEach((seg) => {
    path += `/${seg}`;
    crumbs.push({
      to: path,
      label: labels[seg] || humanise(seg),
    });
  });

  const isDark = variant === "dark";
  const baseText = isDark ? "text-white/70" : "text-brand-grey";
  const hoverText = isDark ? "hover:text-white" : "hover:text-brand-aqua-dark";
  const currentText = isDark ? "text-white" : "text-brand-black";
  const chevronText = isDark ? "text-white/30" : "text-brand-light-grey";

  return (
    <nav
      aria-label="Breadcrumb"
      className={`text-xs ${baseText} flex items-center gap-1.5 flex-wrap ${className}`}
      data-testid={testId}
    >
      <Link
        to="/"
        className={`inline-flex items-center gap-1 ${hoverText} transition-colors`}
        aria-label="Home"
      >
        <Home size={12} />
        <span className="hidden sm:inline">Home</span>
      </Link>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={c.to} className="inline-flex items-center gap-1.5">
            <ChevronRight size={12} className={`${chevronText} flex-shrink-0`} aria-hidden="true" />
            {isLast ? (
              <span
                className={`font-semibold ${currentText} truncate max-w-[40vw] sm:max-w-none`}
                aria-current="page"
              >
                {c.label}
              </span>
            ) : (
              <Link
                to={c.to}
                className={`${hoverText} transition-colors truncate max-w-[30vw] sm:max-w-none`}
              >
                {c.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
